import { Injectable } from '@angular/core';

/**
 * Device types for analytics
 */
export type DeviceType = 'desktop' | 'mobile' | 'tablet';

/**
 * DeviceDetectionService
 *
 * Detects user's device type based on User Agent and screen width.
 * Used for video view analytics.
 *
 * Detection logic:
 * 1. Check User Agent for specific patterns
 * 2. Fallback to screen width if User Agent is ambiguous
 */
@Injectable({
  providedIn: 'root'
})
export class DeviceDetectionService {

  constructor() {}

  /**
   * Detect device type
   *
   * @returns 'desktop' | 'mobile' | 'tablet'
   */
  getDeviceType(): DeviceType {
    const ua = navigator.userAgent.toLowerCase();
    const width = window.innerWidth;

    // Tablet detection (iPad, Android tablets, etc.)
    if (/(ipad|tablet|playbook|silk)|(android(?!.*mobile))/i.test(ua)) {
      console.log('[DeviceDetection] Detected: tablet (UA match)');
      return 'tablet';
    }

    // Mobile detection (smartphones)
    if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
      console.log('[DeviceDetection] Detected: mobile (UA match)');
      return 'mobile';
    }

    // Fallback: detect by screen width
    if (width <= 768) {
      console.log('[DeviceDetection] Detected: mobile (width fallback)');
      return 'mobile';
    } else if (width <= 1024) {
      console.log('[DeviceDetection] Detected: tablet (width fallback)');
      return 'tablet';
    }

    console.log('[DeviceDetection] Detected: desktop');
    return 'desktop';
  }

  /**
   * Get detailed device information (for debugging)
   *
   * @returns object with device information
   */
  getDeviceInfo(): {
    type: DeviceType;
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    pixelRatio: number;
    platform: string;
  } {
    return {
      type: this.getDeviceType(),
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio,
      platform: navigator.platform
    };
  }

  /**
   * Check if device is mobile (mobile or tablet)
   *
   * @returns true if mobile or tablet
   */
  isMobileDevice(): boolean {
    const type = this.getDeviceType();
    return type === 'mobile' || type === 'tablet';
  }

  /**
   * Check if device is desktop
   *
   * @returns true if desktop
   */
  isDesktop(): boolean {
    return this.getDeviceType() === 'desktop';
  }
}
